import { BackButton } from '@/components';
import { Button, Text } from '@/components/atoms';
import { ErrorAlert, LoadingAlert, SuccessAlert } from '@/components/molecules';
import { useAuth } from '@/components/providers/preauth/AuthProvider';
import { URLS } from '@/utils/constants';
import { useRouter } from 'next/router';
import { FC, useEffect, useState } from 'react';
interface Props {
  email: string;
  onBackToSignIn: () => void;
  onBackClick?: () => void;
}
const EmailVerificationMessage: FC<Props> = ({
  email,
  onBackToSignIn,
  onBackClick,
}) => {
  const { resendVerificationEmail, isEmailVerified } = useAuth();
  const router = useRouter();
  const [resendStatus, setResendStatus] = useState<
    'idle' | 'sending' | 'sent' | 'error'
  >('idle');
  const [isVerified, setIsVerified] = useState(false);
  useEffect(() => {
    const checkVerification = async () => {
      const verified = await isEmailVerified();
      setIsVerified(verified);
      if (verified) {
        router.replace(URLS.home);
      }
    };
    checkVerification();
    const intervalId = setInterval(async () => {
      await checkVerification();
    }, 5000);
    return () => clearInterval(intervalId);
  }, [isEmailVerified, router]);
  const handleResendEmail = async () => {
    setResendStatus('sending');
    try {
      const success = await resendVerificationEmail();
      setResendStatus(success ? 'sent' : 'error');
    } catch (error) {
      setResendStatus('error');
    }
  };
  return (
    <div className="flex flex-col gap-20 w-full">
      <div className="flex flex-col gap-md">
        {onBackClick && <BackButton onBackClick={onBackClick} />}
      </div>
      <div className="flex flex-col gap-10">
        <Text value="Verify your email" size="h1" />
        <Text
          value={`We've sent a verification email to ${email}. Please check your inbox and click the verification link to activate your account. If you don't see the email, check your spam folder.`}
        />
        {isVerified ? (
          <div className="flex flex-col gap-4">
            <LoadingAlert
              title="Email Verified"
              description="Your email has been verified successfully. Redirecting you to the dashboard..."
            />
          </div>
        ) : (
          <div className="flex flex-col gap-sm">
            {resendStatus === 'idle' && (
              <Button onClick={handleResendEmail} intent="secondary" fullWidth>
                Resend
              </Button>
            )}
            {resendStatus === 'sending' && (
              <Button loading intent="secondary" fullWidth>
                Send
              </Button>
            )}
            {resendStatus === 'sent' && (
              <div className="flex flex-col gap-4">
                <SuccessAlert
                  title="Email Sent"
                  description="Verification email has been sent successfully."
                />
                <Button
                  onClick={handleResendEmail}
                  intent="secondary"
                  fullWidth
                >
                  Send again
                </Button>
              </div>
            )}
            {resendStatus === 'error' && (
              <div className="flex flex-col gap-4">
                <ErrorAlert
                  title="Failed to Send"
                  description="Failed to send verification email. Please try again."
                />
                <Button
                  onClick={handleResendEmail}
                  intent="secondary"
                  fullWidth
                >
                  Try again
                </Button>
              </div>
            )}
            <div className="flex gap-2 items-baseline">
              <Text value="Verified?" color={`tertiary`} />
              <Text
                value="Sign in"
                intent="link"
                color="accent"
                onClick={onBackToSignIn}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default EmailVerificationMessage;
